import React, { useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Trash2, ChevronUp, ChevronDown, Star, Type, ListChecks } from 'lucide-react';
import { toast } from 'sonner';

const rid = (p) => `${p}_${Math.random().toString(36).slice(2, 9)}`;

export const SCALE_PRESETS = {
  agreement: { name: 'Agreement', labels: ['Strongly Agree', 'Agree', 'Neither Agree nor Disagree', 'Disagree', 'Strongly Disagree'] },
  clarity: { name: 'Clarity', labels: ['Very Clear', 'Clear', 'Neutral', 'Somewhat Unclear', 'Not Clear'] },
  quality: { name: 'Quality', labels: ['Excellent', 'Good', 'Neutral', 'Poor', 'Very Poor'] },
};

const scaleFromLabels = (labels) => labels.map((label, i) => ({ value: 5 - i, label }));
export const emptyLikert = () => ({ id: rid('q'), type: 'likert', label: '', required: true });
export const emptyText = (subtype = 'short') => ({ id: rid('q'), type: 'text', subtype, label: '', required: true });
export const emptySection = () => ({ id: rid('sec'), heading: '', description: '', scale: scaleFromLabels(SCALE_PRESETS.agreement.labels), items: [emptyLikert()] });

// Convert any stored template (new sections OR legacy flat questions) into editor sections
export const templateToSections = (t) => {
  if (t?.sections && t.sections.length) {
    return t.sections.map(s => ({
      id: s.id || rid('sec'), heading: s.heading || '', description: s.description || '',
      scale: (s.scale && s.scale.length ? s.scale : scaleFromLabels(SCALE_PRESETS.agreement.labels)),
      items: (s.items || []).map(it => ({ id: it.id || rid('q'), type: it.type === 'text' ? 'text' : 'likert', subtype: it.subtype || 'short', label: it.label || '', required: it.required !== false })),
    }));
  }
  const qs = t?.questions || [];
  if (qs.length) {
    return [{
      id: rid('sec'), heading: 'Feedback', description: '',
      scale: scaleFromLabels(SCALE_PRESETS.agreement.labels),
      items: qs.map(q => ({
        id: q.id || rid('q'),
        type: q.type === 'text' ? 'text' : 'likert',
        subtype: 'short',
        label: q.label || '',
        required: q.required !== false,
      })),
    }];
  }
  return [emptySection()];
};

export const FeedbackTemplateEditor = ({ initial, saving, onSubmit, onCancel, submitLabel = 'Save Template' }) => {
  const [name, setName] = useState(initial?.name || '');
  const [intro, setIntro] = useState(initial?.intro_message || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [sections, setSections] = useState(() => templateToSections(initial));

  const patchSection = (si, patch) => setSections(prev => prev.map((s, i) => i === si ? { ...s, ...patch } : s));
  const addSection = () => setSections(prev => [...prev, emptySection()]);
  const removeSection = (si) => setSections(prev => prev.length > 1 ? prev.filter((_, i) => i !== si) : prev);
  const moveSection = (si, dir) => setSections(prev => {
    const j = si + dir;
    if (j < 0 || j >= prev.length) return prev;
    const arr = [...prev];[arr[si], arr[j]] = [arr[j], arr[si]];
    return arr;
  });

  const applyPreset = (si, key) => patchSection(si, { scale: scaleFromLabels(SCALE_PRESETS[key].labels) });
  const setScaleLabel = (si, value, label) => patchSection(si, { scale: sections[si].scale.map(sp => sp.value === value ? { ...sp, label } : sp) });

  const addItem = (si, item) => patchSection(si, { items: [...sections[si].items, item] });
  const patchItem = (si, ii, patch) => patchSection(si, { items: sections[si].items.map((it, i) => i === ii ? { ...it, ...patch } : it) });
  const removeItem = (si, ii) => patchSection(si, { items: sections[si].items.filter((_, i) => i !== ii) });

  const submit = () => {
    if (!name.trim()) { toast.error('Form title is required'); return; }
    const cleanSections = sections.map(s => ({
      ...s,
      heading: (s.heading || '').trim(),
      items: (s.items || []).filter(it => (it.label || '').trim()),
    })).filter(s => s.items.length > 0);
    if (cleanSections.length === 0) { toast.error('Add at least one section with a question'); return; }
    for (const s of cleanSections) {
      if (!s.heading) { toast.error('Every section needs a heading'); return; }
      if (s.scale.some(sp => !(sp.label || '').trim())) { toast.error(`Fill all 5 scale labels in "${s.heading}"`); return; }
    }
    onSubmit({
      name: name.trim(), description, intro_message: intro,
      sections: cleanSections.map(s => ({
        id: s.id, heading: s.heading, description: (s.description || '').trim(),
        scale: s.scale.map(sp => ({ value: sp.value, label: sp.label.trim() })),
        items: s.items.map(it => ({ id: it.id, type: it.type, subtype: it.type === 'text' ? (it.subtype || 'short') : null, label: it.label.trim(), required: !!it.required })),
      })),
    });
  };

  return (
    <div className="space-y-5" data-testid="template-editor">
      <div><Label>Form Title *</Label><Input value={name} onChange={e => setName(e.target.value)} data-testid="template-name-input" placeholder="e.g. New Joiner Induction Feedback" className="mt-1.5" /></div>
      <div><Label>Intro / Welcome message</Label><Textarea value={intro} onChange={e => setIntro(e.target.value)} data-testid="template-intro-input" placeholder="e.g. Welcome Candidate! Please share your honest feedback about your induction experience." className="mt-1.5" rows={2} /></div>
      <div><Label>Internal note (optional)</Label><Input value={description} onChange={e => setDescription(e.target.value)} data-testid="template-desc-input" placeholder="Only visible to HR" className="mt-1.5" /></div>

      <div className="space-y-4">
        {sections.map((s, si) => (
          <div key={s.id} className="border-2 rounded-xl p-4 space-y-4 bg-muted/20" data-testid={`section-${si}`}>
            <div className="flex items-start gap-2">
              <div className="flex-1 space-y-2">
                <Input value={s.heading} onChange={e => patchSection(si, { heading: e.target.value })} data-testid={`section-heading-${si}`} placeholder={`Section ${si + 1} heading — e.g. HR Induction & Policy Understanding`} className="font-semibold" />
                <Input value={s.description} onChange={e => patchSection(si, { description: e.target.value })} data-testid={`section-desc-${si}`} placeholder="Optional instruction / description line" className="text-sm" />
              </div>
              <div className="flex flex-col gap-1">
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => moveSection(si, -1)} disabled={si === 0} data-testid={`section-up-${si}`}><ChevronUp className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => moveSection(si, 1)} disabled={si === sections.length - 1} data-testid={`section-down-${si}`}><ChevronDown className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeSection(si)} data-testid={`section-delete-${si}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
              </div>
            </div>

            <div className="rounded-lg border bg-background p-3 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-muted-foreground mr-1">Rating scale:</span>
                {Object.entries(SCALE_PRESETS).map(([key, p]) => (
                  <Button key={key} size="sm" variant="outline" className="h-7 text-xs" onClick={() => applyPreset(si, key)} data-testid={`section-${si}-preset-${key}`}>{p.name}</Button>
                ))}
                <span className="text-[11px] text-muted-foreground ml-1">(labels stay editable)</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                {s.scale.map(sp => (
                  <div key={sp.value} className="flex items-center gap-1.5">
                    <span className="text-xs font-bold w-4 text-primary">{sp.value}</span>
                    <Input value={sp.label} onChange={e => setScaleLabel(si, sp.value, e.target.value)} data-testid={`section-${si}-scale-${sp.value}`} className="h-8 text-xs" />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              {s.items.map((it, ii) => (
                <div key={it.id} className="flex items-start gap-2 rounded-lg border p-2 bg-background" data-testid={`section-${si}-item-${ii}`}>
                  <div className="mt-2">
                    {it.type === 'likert' ? <Star className="h-4 w-4 text-amber-500" /> : <Type className="h-4 w-4 text-sky-600" />}
                  </div>
                  <div className="flex-1 space-y-1.5">
                    <Input value={it.label} onChange={e => patchItem(si, ii, { label: e.target.value })} data-testid={`section-${si}-item-${ii}-label`} placeholder={it.type === 'likert' ? 'Statement rated on the scale above' : 'Open-ended question'} />
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{it.type === 'likert' ? 'Rating (uses section scale)' : 'Open-ended'}</span>
                      {it.type === 'text' && (
                        <Select value={it.subtype || 'short'} onValueChange={v => patchItem(si, ii, { subtype: v })}>
                          <SelectTrigger className="h-7 w-32 text-xs" data-testid={`section-${si}-item-${ii}-subtype`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="short">Short answer</SelectItem>
                            <SelectItem value="long">Paragraph</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      <label className="flex items-center gap-1.5 text-xs"><Checkbox checked={it.required} onCheckedChange={v => patchItem(si, ii, { required: !!v })} data-testid={`section-${si}-item-${ii}-required`} /> Required</label>
                    </div>
                  </div>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeItem(si, ii)} data-testid={`section-${si}-item-${ii}-delete`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => addItem(si, emptyLikert())} data-testid={`section-${si}-add-rating`}><ListChecks className="h-4 w-4 mr-1" /> Add rating statement</Button>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => addItem(si, emptyText())} data-testid={`section-${si}-add-text`}><Type className="h-4 w-4 mr-1" /> Add open-ended question</Button>
              </div>
            </div>
          </div>
        ))}
        <Button variant="outline" onClick={addSection} data-testid="add-section-btn"><Plus className="h-4 w-4 mr-1" /> Add Section</Button>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button onClick={submit} disabled={saving} data-testid="save-template-btn">{submitLabel}</Button>
      </div>
    </div>
  );
};
